"use client";

import ProductLink from "@/components/navigation/ProductLink";
import { useMemo } from "react";
import type { Product } from "@/types";
import type { StationSummary } from "@/hooks/useStationHubData";
import {
  BIKE_CLASSES,
  bikeLicenseText,
  getBikeClass,
  modelKeyOf,
  type BikeClassKeyOrUnknown,
} from "@/lib/mobility/bike-classes";
import { periodTotal } from "@/lib/mobility/pricing";
import { summarizeProducts } from "@/lib/mobility/station-data";
import { cn } from "@/lib/utils";
import { pickLabel, useHub, useHubDensity } from "./hub-context";
import { HUB_GUTTER, HubCell, hubListClass, SectionHeader } from "./hub-ui";
import { ItemRow } from "./ItemRow";
import { CountSortRow, SheetPageLink, StationHeadCard } from "./StationSheetParts";
import { useWindowedCount } from "./useWindowedCount";

/** 차종 행에 보여줄 기간(일) — 모델 시트 요금 그리드와 같은 1·3·7 */
const ROW_PERIODS = [1, 3, 7] as const;

type ListItem =
  | { kind: "header"; key: string; title: string; meta: string }
  | { kind: "row"; key: string; row: BikeRow; classKey: BikeClassKeyOrUnknown };

interface BikeRow {
  /** 요금제 접미사를 뗀 모델명 */
  name: string;
  product: Product;
  price: number;
}

/**
 * 지점 시트 — 지점을 누르면(핀·목록 행·모델 시트의 지점 행) 곧바로 열린다.
 * 머리는 회색 카드(사진·이름·찾아오는 길 · 도시), 그 아래 "차종 N" + 예약 안내,
 * 클래스별 얇은 헤더(면허 한 줄) + 차종 행. 행을 누르면 그 지점에 연결된 상품
 * 상세로 간다. 필터 축(배기량·타입·라이딩·변속)은 지도 위 알약이 정본이라 시트
 * 안에는 칩을 두지 않는다 — 여기 보이는 상품도 그 축을 적용한 것이다.
 *
 * 바이크 전용이다 — 캠핑장은 `CampingStationSheet`(같은 머리 카드, 장비 사진 그리드).
 */
export function StationBikesSheet({ station }: { station: StationSummary }) {
  const {
    t,
    locale,
    productHref,
    stationHref,
    formatPrice,
    visibleProducts,
    facetsOf,
    sortBy,
  } = useHub();
  const products = visibleProducts(station);
  const pageHref = stationHref(station.id);
  // 데스크톱 목록 열에서는 차종도 2열 카드(09-23 A안).
  const wide = useHubDensity() === "wide";
  const spanClass = wide ? "col-span-2" : undefined;

  const summary = useMemo(
    () => summarizeProducts(products, facetsOf),
    [products, facetsOf],
  );


  // 클래스별 차종 행. 같은 모델의 요금제 상품이 둘이면 싼 쪽만 남긴다.
  const items = useMemo<ListItem[]>(() => {
    const byClass = new Map<string, Map<string, BikeRow>>();

    for (const product of products) {
      const key = facetsOf(product.id)?.classKey ?? "unknown";
      const rows = byClass.get(key) ?? new Map<string, BikeRow>();
      const name = modelKeyOf(product);
      const price = periodTotal(product, 1) ?? 0;
      const existing = rows.get(name);
      if (!existing || (price > 0 && price < existing.price)) {
        rows.set(name, { name, product, price });
      }
      byClass.set(key, rows);
    }

    const sectionOf = (key: BikeClassKeyOrUnknown) => {
      const cls = getBikeClass(key);
      return {
        key,
        title: cls ? pickLabel(cls.label, locale) : "",
        meta: bikeLicenseText(key, locale) ?? "",
        rows: [...(byClass.get(key) ?? new Map<string, BikeRow>()).values()],
      };
    };

    const ordered = [
      ...BIKE_CLASSES.filter((c) => byClass.has(c.key)).map((c) =>
        sectionOf(c.key)
      ),
      ...(byClass.has("unknown") ? [sectionOf("unknown")] : []),
    ];

    // 검색 정렬이 걸려 있으면 섹션 없이 한 목록으로 편다 —
    // 가격 낮은순/높은순은 1일가, 최신순은 지점 목록과 같은 입력 순서(승인순) 유지.
    if (sortBy) {
      const rows = ordered.flatMap((section) =>
        section.rows.map((row) => ({ row, classKey: section.key })),
      );
      if (sortBy === "price_low" || sortBy === "price_high") {
        const dir = sortBy === "price_low" ? 1 : -1;
        rows.sort((a, b) => {
          const ap = a.row.price > 0 ? a.row.price : Number.POSITIVE_INFINITY;
          const bp = b.row.price > 0 ? b.row.price : Number.POSITIVE_INFINITY;
          return (ap - bp) * dir;
        });
      }
      return rows.map(({ row, classKey }) => ({
        kind: "row" as const,
        key: row.product.id,
        row,
        classKey,
      }));
    }

    // 섹션 헤더 + 차종 행을 한 배열로 펴서 24개씩 창으로 그린다.
    const flat: ListItem[] = [];
    for (const section of ordered) {
      if (section.title) {
        flat.push({
          kind: "header",
          key: `h:${section.key}`,
          title: section.title,
          meta: section.meta,
        });
      }
      for (const row of section.rows) {
        flat.push({
          kind: "row",
          key: row.product.id,
          row,
          classKey: section.key,
        });
      }
    }
    return flat;
  }, [products, facetsOf, locale, sortBy]);

  const { visible, hasMore, attachSentinel } = useWindowedCount(items.length);

  return (
    <div className="pb-8">
      {/* 예약 안내 — 지점 카드 위, 주황 강조 (09-22 사용자 결정). */}
      <p
        className={cn(
          HUB_GUTTER,
          // 위 간격은 트리(모바일·태블릿·데스크톱)가 16px로 똑같이 준다 — 여기선 0
          "pb-2 text-[13px] font-semibold text-orange-600 mapShow:text-sm",
        )}
      >
        {t("hub.note")}
      </p>

      <div className={HUB_GUTTER}>
        <StationHeadCard station={station} />
      </div>

      {/* 지점 페이지가 있는 지점만(10-15) — 시트와 별개의 독립 페이지로 가는 실제 링크 */}
      {pageHref ? (
        <div className={cn(HUB_GUTTER, "pt-2.5")}>
          <SheetPageLink href={pageHref}>{t("hub.stationPageLink")}</SheetPageLink>
        </div>
      ) : null}

      <CountSortRow label={t("hub.bikesN", { n: summary.modelCount })} />

      {items.length === 0 ? (
        <p
          className={`${HUB_GUTTER} py-10 text-center text-[13px] text-gray-500`}
        >
          {t("hub.noResult")}
        </p>
      ) : (
        <div
          className={hubListClass(
            wide,
            // 테두리·구분선 대신 여백으로 구분(09-29, 지점 목록과 같은 규칙)
            "flex flex-col gap-4",
            "grid grid-cols-2 items-start gap-x-5 gap-y-7",
          )}
        >
          {items.slice(0, visible).map((item) => {
            if (item.kind === "header") {
              return (
                <SectionHeader
                  key={item.key}
                  title={item.title}
                  meta={item.meta}
                  thin
                  className={spanClass}
                />
              );
            }
            const facets = facetsOf(item.row.product.id);
            return (
              <HubCell key={item.key} wide={wide} bare>
              <ProductLink
                href={productHref(item.row.product.id, station.id)}
                className="block transition hover:bg-gray-50"
              >
                <ItemRow
                  image={item.row.product.images?.[0]}
                  name={item.row.name}
                  classKey={item.classKey}
                  bikeType={facets?.bikeType}
                  transmission={facets?.transmission}
                  rideStyles={facets?.rideStyles}
                  periodPrices={ROW_PERIODS.map((days) => {
                    const total = periodTotal(item.row.product, days);
                    return {
                      label: t("hub.priceHead.d" + days),
                      value:
                        total != null && total > 0
                          ? formatPrice(total, item.row.product.currency)
                          : null,
                    };
                  })}
                />
              </ProductLink>
              </HubCell>
            );
          })}
          {hasMore ? (
            <div ref={attachSentinel} className={`h-8 ${spanClass ?? ""}`} />
          ) : null}
        </div>
      )}
    </div>
  );
}
